import { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { debounce } from 'lodash';
import { X } from 'lucide-react';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Skeleton from '../../../components/ui/skeleton';
import Heading from '../../../components/Heading';
import OtherCard from './OtherProductCard';
import useOtherProduct from '../../../stores/useOtherProduct';
import { LanguageContext } from '../../../contexts/LanguageContext';
import { IOtherProduct } from '../../../types/types';
import { FormatPrice } from '../../../utils/format';
import Separator from '../../../components/ui/separator';

export function OtherSlider({ className = '' }) {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const {
    productsBySrv,
    fetchLoading,
    fetchProductsByServiceId,
    pageBySrv,
    hasMoreBySrv,
    isLoadingNextPage,
    cache,
    currentServiceId,
  } = useOtherProduct();
  const [slidesPerView, setSlidesPerView] = useState(1.5);
  const [selectedProduct, setSelectedProduct] = useState<IOtherProduct | null>(null);
  const [cardWidth, setCardWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperRef>(null);
  const { id } = useParams();
  const serviceId = Number(id);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const SERVICE_CARD_GAP = 8;

  const filteredProducts = useMemo(
    () => productsBySrv.filter((product) => product.service_id === serviceId),
    [productsBySrv, serviceId],
  );

  useEffect(() => {
    const cacheKey = `${serviceId}-1`;
    if (serviceId !== currentServiceId || filteredProducts.length === 0) {
      if (cache[cacheKey]) {
        fetchProductsByServiceId(1, false, serviceId);
      } else {
        fetchProductsByServiceId(1, true, serviceId).then(() => setIsInitialLoad(false));
      }
    } else {
      setIsInitialLoad(false);
    }
  }, [fetchProductsByServiceId, serviceId, cache, currentServiceId, filteredProducts.length]);

  const updateLayout = useCallback(() => {
    if (!containerRef.current) return;

    const width = window.innerWidth;
    let newCardWidth = 250;
    if (width < 370) newCardWidth = 158;
    else if (width < 640) newCardWidth = 150;
    else if (width < 1024) newCardWidth = 180;

    setCardWidth(newCardWidth);

    const containerWidth = containerRef.current.offsetWidth;
    const calculatedSlides = containerWidth / (newCardWidth + SERVICE_CARD_GAP);
    let slidesToSet = Math.floor(calculatedSlides) || 2;

    if (width < 300) slidesToSet = 2;
    else if (width < 400) slidesToSet = 2;
    else if (width < 620) slidesToSet = 2.5;
    else if (width < 700) slidesToSet = 3;
    else if (width < 980) slidesToSet = 3.5;
    else if (width > 980) slidesToSet = 5;

    setSlidesPerView(slidesToSet);

    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.update();
    }
  }, []);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, filteredProducts]);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.update();
    }
  }, [filteredProducts, slidesPerView]);

  const handleReachEnd = useMemo(
    () =>
      debounce(() => {
        const cacheKey = `${serviceId}-${pageBySrv + 1}`;
        if (
          filteredProducts.length >= slidesPerView &&
          hasMoreBySrv &&
          !fetchLoading &&
          !isLoadingNextPage &&
          !cache[cacheKey]
        ) {
          console.log('Fetching next page:', pageBySrv + 1);
          fetchProductsByServiceId(pageBySrv + 1, false, serviceId);
        }
      }, 300),
    [
      filteredProducts.length,
      slidesPerView,
      hasMoreBySrv,
      fetchLoading,
      isLoadingNextPage,
      pageBySrv,
      serviceId,
      cache,
      fetchProductsByServiceId,
    ],
  );

  const handleCardClick = (product: IOtherProduct) => {
    setSelectedProduct(product);
  };

  const closePopup = () => {
    setSelectedProduct(null);
  };

  const skeletonSlides = Array.from({ length: Math.ceil(slidesPerView) * 2 }, (_, i) => (
    <SwiperSlide key={`skeleton-${i}`} style={{ width: cardWidth }}>
      <Skeleton className={`shadow-md h-48 sm:h-64 w-[${cardWidth}px]`} />
    </SwiperSlide>
  ));

  const productSlides = filteredProducts.map((product) => (
    <SwiperSlide key={product.product_id} className="pb-12" style={{ width: cardWidth }}>
      <button type="button" onClick={() => handleCardClick(product)} className="cursor-pointer w-full">
        <OtherCard
          name_prd={lang ? product.name_prd : product.name_prd_en || product.name_prd}
          price_prd={product.price_prd}
          image_prd={product.image_prd || '/assets/no-data-image.svg'}
          className="w-full"
        />
      </button>
    </SwiperSlide>
  ));

  if (!fetchLoading && productsBySrv.length === 0) return null;

  return (
    <section
      ref={containerRef}
      className={`${className} relative ${
        filteredProducts.length === 0 ? 'hidden' : 'flex'
      } flex-col sm:text-md items-center gap-4 overflow-hidden mt-4 md:mt-8`}
    >
      <div className="flex flex-col gap-1 mb-4 sm:text-base text-sm text-center">
        <Heading>{t('tagline.detail-service.other.title')}</Heading>
        <p>{t('tagline.detail-service.other.tagline')}</p>
      </div>
      <div className="relative w-[90vw] max-w-screen-xl">
        <Swiper
          ref={swiperRef}
          modules={[Pagination]}
          pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 3 }}
          centerInsufficientSlides
          slidesPerView={slidesPerView}
          spaceBetween={SERVICE_CARD_GAP}
          onSwiper={(swiper) => {
            if (swiperRef.current) swiperRef.current.swiper = swiper;
          }}
          onReachEnd={handleReachEnd}
        >
          {fetchLoading && filteredProducts.length === 0
            ? skeletonSlides
            : [
                ...productSlides,
                ...(isLoadingNextPage
                  ? [
                      <SwiperSlide key="loading" style={{ width: cardWidth }}>
                        <Skeleton className={`shadow-md h-48 sm:h-64 w-[${cardWidth}px]`} />
                      </SwiperSlide>,
                    ]
                  : []),
              ]}
        </Swiper>
      </div>

      {/* Popup Component */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black backdrop-blur-sm animate-fade-in-up duration-200 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-screen-md w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Bagian Sticky */}
            <div className="sticky top-0 bg-white z-10 flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {lang ? selectedProduct.name_prd : selectedProduct.name_prd_en || selectedProduct.name_prd}
              </h3>
              <button type="button" onClick={closePopup} className="text-gray-500 hover:text-gray-700">
                <X />
              </button>
            </div>
            {/* Bagian yang di-scroll */}
            <div className="overflow-y-auto">
              <img
                src={selectedProduct.image_prd || '/assets/no-data-image.svg'}
                alt={selectedProduct.name_prd}
                className="w-full mt-4 h-40 object-cover rounded-md mb-4"
              />
              <h4 className="mb-2 text-[0.65rem]">
                {t('general.startfrom')}:
                <p className="text-secondary text-sm sm:text-base">IDR {FormatPrice(selectedProduct.price_prd)}</p>
              </h4>
              <Separator />
              <div
                className="mt-2 text-xs sm:text-sm prose prose-sm md:prose-base lg:prose-lg max-w-none"
                dangerouslySetInnerHTML={{
                  __html: lang
                    ? selectedProduct.detail_prd
                    : selectedProduct.detail_prd_en || selectedProduct.detail_prd,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
